/**
 * @fileoverview Renders list of verification methods.
 *
 * @author Kuber Sethi (ksethi@yext.com)
 */

import {SelectableCardGrid} from '/src/com/yext/listingslaunchstorm/js/components/common/selectablecardgrid';
import {ListingsLaunchContext} from '/src/com/yext/listingslaunchstorm/js/contexts/listingslaunchcontextprovider';
import {EntityInfo} from '/src/com/yext/listingslaunchstorm/js/data/entityinfo';
import {VerificationMethod, getVerificationMethodDisplayName} from '/src/com/yext/listingslaunchstorm/js/data/verificationmethod';
import {VerifiedListing} from '/src/com/yext/listingslaunchstorm/js/data/verifiedlisting';
import {Button} from '/ui/components/button/button';
import {useIsSmallViewport} from '/ui/lib/responsivity';

import * as styles from '/src/com/yext/listingslaunchstorm/js/components/styles/verifyentity.module.scss';
import EmailSvg from '/src/com/yext/listingslaunchstorm/public/icons/email.svg';
import GreyCheckmark from '/src/com/yext/listingslaunchstorm/public/icons/grey-checkmark.svg';
import LetterSvg from '/src/com/yext/listingslaunchstorm/public/icons/letter.svg';
import LinkSvg from '/src/com/yext/listingslaunchstorm/public/icons/link.svg';
import PhoneSvg from '/src/com/yext/listingslaunchstorm/public/icons/phone.svg';
import SmartDeviceSvg from '/src/com/yext/listingslaunchstorm/public/icons/smart-device.svg';
import WarningShieldSvg from '/src/com/yext/listingslaunchstorm/public/icons/warning-shield.svg';

/**
 * @param {{
 *  entity: !EntityInfo,
 *  verificationMethods: Array<VerificationMethod>,
 *  setAlreadyHasVerificationCode: function(boolean),
 *  selectedVerificationMethod: VerificationMethod,
 *  setSelectedVerificationMethod: function(VerificationMethod),
 *  setShouldSkipVerification: function(boolean),
 *  renderFlowContent: function(VerificationMethod): React.ReactNode,
 * }} props
 * @return {React.ReactNode}
 */
const VerificationMethods = ({
  entity,
  verificationMethods,
  setAlreadyHasVerificationCode,
  selectedVerificationMethod,
  setSelectedVerificationMethod,
  setShouldSkipVerification,
  renderFlowContent,
}) => {
  const {setVerifiedListings} = React.useContext(ListingsLaunchContext);
  const isSmallViewport = useIsSmallViewport();

  const getOtherVerifiedListings = currentVerifiedListings => (
    currentVerifiedListings.filter(verification => verification.entityId !== entity.id)
  );

  const onSelect = value => {
    if (value === VerificationMethod.SKIP) {
      return handleSkipVerification();
    }

    setAlreadyHasVerificationCode(value === VerificationMethod.CODE);
    setSelectedVerificationMethod(value ?? VerificationMethod.OTHER);
    setShouldSkipVerification(false);
    setVerifiedListings(getOtherVerifiedListings);
  };

  // When a user skips verification, we need to update the verification states in the context. For
  // other methods, this is done once a code is actually input, but this method has no further steps
  // and should allow the user to continue immediately
  const handleSkipVerification = () => {
    setAlreadyHasVerificationCode(false);
    setShouldSkipVerification(true);
    setSelectedVerificationMethod(VerificationMethod.SKIP);

    const newVerification = new VerifiedListing({
      entityId: entity.id,
      method: VerificationMethod.SKIP,
    });

    setVerifiedListings(currentVerifiedListings => [
      ...getOtherVerifiedListings(currentVerifiedListings),
      newVerification,
    ]);
  };

  const isSelected = verificationMethod => {
    return verificationMethod != null && verificationMethod === selectedVerificationMethod;
  };

  const getCardContent = isSmallViewport ? renderFlowContent : undefined;
  const skipComponent = isSmallViewport ? (
      getVerificationMethodCard(VerificationMethod.SKIP, isSelected, getCardContent)
    ) : (
    <div className={styles.existingVerificationCode}>
      <Button
        chromeless
        pendoId={getPendoIdForVerificationMethod(VerificationMethod.SKIP)}
        onClick={handleSkipVerification}
      >
        {yext.msg('Skip Verification')}
      </Button>
    </div>
  );

  return (
    <div className={styles.verificationMethods}>
      <SelectableCardGrid
        size={isSmallViewport ? 1 : (verificationMethods?.length ?? 1)}
        onSelected={onSelect}
        isSelected={isSelected}
        interactive
      >
        <SelectableCardGrid.Header title={yext.msg('Verification Options')} description={yext.msg('Select a verification option')} />
        {verificationMethods.map(m => getVerificationMethodCard(m, isSelected, getCardContent))}
        {skipComponent}
      </SelectableCardGrid>
    </div>
  );
};

function getVerificationMethodCard(method, isSelected, getContent) {
  return (
    <SelectableCardGrid.Card
      key={`${method}`}
      pendoId={getPendoIdForVerificationMethod(method)}
      value={VerificationMethod[method]}
    >
      <div className={styles.verificationMethod}>
        <div className={styles.verificationMethodIcon}>
          {getIconForVerificationMethod(method) ?? <WarningShieldSvg />}
        </div>
        <div>
          {getVerificationMethodDisplayName(VerificationMethod[method]) ?? yext.msg('Skip')}
        </div>
      </div>
      {(isSelected(method) && getContent) && (
        <div className={styles.verificationContent}>{getContent(method)}</div>
      )}
    </SelectableCardGrid.Card>
  );
}

export function getIconForVerificationMethod(method) {
  switch (method) {
    case VerificationMethod.EMAIL: return <EmailSvg />;
    case VerificationMethod.PHONE_CALL: return <PhoneSvg />;
    case VerificationMethod.SMS: return <SmartDeviceSvg />;
    case VerificationMethod.VIDEO: return <LinkSvg />;
    case VerificationMethod.POSTCARD: return <LetterSvg />;
    case VerificationMethod.OTHER: return <WarningShieldSvg />;
    case VerificationMethod.CODE: return <GreyCheckmark />;
    case VerificationMethod.SKIP: return <WarningShieldSvg />;
    default: return <WarningShieldSvg />;
  }
}

function getPendoIdForVerificationMethod(method) {
  switch (method) {
    case VerificationMethod.SMS:
      return 'listings-launch-flow-verifying-text-message';
    case VerificationMethod.EMAIL:
      return 'listings-launch-flow-verifying-email';
    case VerificationMethod.PHONE_CALL:
      return 'listings-launch-flow-verifying-phone-call';
    case VerificationMethod.VIDEO:
      return 'listings-launch-flow-verifying-video';
    case VerificationMethod.POSTCARD:
      return 'listings-launch-flow-verifying-postcard';
    case VerificationMethod.CODE:
      return 'listings-launch-flow-verifying-input-existing-code';
    case VerificationMethod.SKIP:
      return 'listings-launch-flow-verifying-skip';
    default:
      return undefined;
  }
}

export default VerificationMethods;
